import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api } from "@/lib/api";

const SeekerAuthContext = createContext(null);

/**
 * Seekers are stored in `users` with role='seeker' and use the same JWT cookie.
 * This context provides a separate `seeker` view so the recruiter portal's
 * AuthContext never tries to load `seeker` into its protected routes.
 */
export function SeekerAuthProvider({ children }) {
    const [seeker, setSeeker] = useState(null);   // null = checking, false = anon, object = logged in
    const [loading, setLoading] = useState(true);

    const fetchMe = useCallback(async () => {
        // Only attempt to resolve a seeker session if we're on a seeker route, OR
        // the public apply page (/j/{slug}, where we show one-tap apply for
        // signed-in seekers), OR a cookie is present. Avoids 401 spam on
        // recruiter/admin/public pages. The access_token cookie is httpOnly,
        // so we treat /j/ paths as seeker routes too — the backend 401's for
        // anon users are harmless and the context falls back to seeker=false.
        const onSeekerRoute = typeof window !== "undefined" &&
            (window.location.pathname.startsWith("/seeker") ||
             window.location.pathname.startsWith("/j/"));
        const hasCookie = typeof document !== "undefined" &&
            /(?:^|;\s*)access_token=/.test(document.cookie);
        if (!onSeekerRoute && !hasCookie) {
            setSeeker(false);
            setLoading(false);
            return;
        }
        try {
            const { data } = await api.get("/seeker/me");
            setSeeker(data);
        } catch {
            setSeeker(false);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchMe(); }, [fetchMe]);

    const login = async (email, password) => {
        const { data } = await api.post("/seeker/login", { email, password });
        setSeeker(data.user);
        return data.user;
    };
    const register = async (payload) => {
        const { data } = await api.post("/seeker/register", payload);
        setSeeker(data.user);
        return data.user;
    };
    const logout = async () => {
        try { await api.post("/auth/logout"); } catch { /* ignore */ }
        setSeeker(false);
    };

    return (
        <SeekerAuthContext.Provider value={{ seeker, loading, login, register, logout, refresh: fetchMe }}>
            {children}
        </SeekerAuthContext.Provider>
    );
}

export function useSeekerAuth() {
    return useContext(SeekerAuthContext);
}
